import {Box, Button, MenuItem, Stack, TextField, Typography} from "@mui/material";
import {useState} from "react";
import {toast} from "react-toastify";
import {c, maxW, section} from "../landingTokens";

const CALL_VOLUMES = ["Under 5,000", "5,000 – 25,000", "25,000 – 100,000", "Over 100,000"];

const CONTACT_EMAIL = "contact@ireason.mk";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: c.bgSoft,
    borderRadius: "12px",
    fontSize: "14.5px",
    "& fieldset": {borderColor: "rgba(44,43,42,0.16)"},
    "&:hover fieldset": {borderColor: "rgba(44,43,42,0.3)"},
    "&.Mui-focused": {bgcolor: c.white},
    "&.Mui-focused fieldset": {borderColor: c.purple, borderWidth: 1},
  },
  "& .MuiInputLabel-root": {fontSize: "13px", color: c.inkSoft},
  "& .MuiInputLabel-root.Mui-focused": {color: c.purpleDeep},
};

const EMPTY = {name: "", phone: "", email: "", company: "", volume: CALL_VOLUMES[1], message: ""};

export const ContactDemo = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sent

  const set = (field) => (e) => setForm((prev) => ({...prev, [field]: e.target.value}));

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    const subject = encodeURIComponent("Book a demo – OpenBrain");
    const body = encodeURIComponent(
      `Name: ${form.name}
Phone: ${form.phone}
Email: ${form.email}
Company: ${form.company || "-"}
Monthly call volume: ${form.volume}

What would you automate first:
${form.message}`
    );

    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    setStatus("sent");
    setForm(EMPTY);
  };

  const label = status === "sent" ? "Thanks — we'll be in touch" : "Book a free demo";

  return (
    <Box component="section" id="contact" data-screen-label="Contact" sx={{px: section.px, pb: section.pb}}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          borderRadius: "34px",
          overflow: "hidden",
          border: `1px solid ${c.line}`,
          display: "grid",
          gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"},
          bgcolor: c.white,
          boxShadow: "0 30px 75px -50px rgba(55, 56, 113, 0.75)",
        }}
      >
        {/* Left: the pitch */}
        <Box
          sx={{
            background: `linear-gradient(160deg, ${c.purple}, ${c.purpleDeep} 60%, ${c.teal})`,
            p: "clamp(32px, 4vw, 56px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box aria-hidden sx={{position: "absolute", bottom: -120, right: -90, width: 320, height: 320, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.16)"}} />
          <Box sx={{position: "relative"}}>
            <Typography component="h2" sx={{fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1.06, letterSpacing: "-0.03em", fontWeight: 600, color: c.white, mb: 1.75, textWrap: "balance"}}>
              See it answer a real call.
            </Typography>
            <Typography sx={{fontSize: 17, lineHeight: 1.5, color: "rgba(255,255,255,0.88)", mb: 4.25}}>
              Book a 30-minute demo. We'll run your own call scenarios through the agent, live.
            </Typography>

            <Stack gap={1.75}>
              {["30-minute call, no slide deck", "Your scripts and your language", "Pilot scoping in the same session"].map((line) => (
                <Typography key={line} sx={{fontSize: "14.5px", color: c.white}}>{line}</Typography>
              ))}
            </Stack>

            <Stack gap={1} sx={{mt: 5.5, pt: 3.25, borderTop: "1px solid rgba(255,255,255,0.28)"}}>
              <Typography component="a" href="tel:+38975753932" sx={{fontSize: "14.5px", color: c.white, textDecoration: "none", "&:hover": {opacity: 0.8}}}>
                +389 75 753 932
              </Typography>
              <Typography component="a" href={`mailto:${CONTACT_EMAIL}`} sx={{fontSize: "14.5px", color: c.white, textDecoration: "none", "&:hover": {opacity: 0.8}}}>
                {CONTACT_EMAIL}
              </Typography>
              <Typography sx={{fontSize: "14.5px", color: "rgba(255,255,255,0.8)"}}>
                3rd Macedonian Brigade 37, Skopje
              </Typography>
            </Stack>
          </Box>
        </Box>

        {/* Right: the form */}
        <Box component="form" onSubmit={handleSubmit} sx={{p: "clamp(32px, 4vw, 56px)", display: "flex", flexDirection: "column", gap: 2}}>
          <Typography sx={{fontSize: "11.5px", letterSpacing: "0.16em", textTransform: "uppercase", color: c.purpleDeep, fontWeight: 600, mb: 0.5}}>
            Book a free demo
          </Typography>
          <Stack direction={{xs: "column", sm: "row"}} gap={2}>
            <TextField label="Name" required fullWidth size="small" value={form.name} onChange={set("name")} placeholder="Your full name" sx={fieldSx} />
            <TextField label="Phone" required fullWidth size="small" value={form.phone} onChange={set("phone")} placeholder="+389 …" sx={fieldSx} />
          </Stack>
          <TextField label="Work email" required fullWidth size="small" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" sx={fieldSx} />
          <TextField label="Company (optional)" fullWidth size="small" value={form.company} onChange={set("company")} placeholder="Your company name" sx={fieldSx} />
          <TextField label="Monthly call volume" select fullWidth size="small" value={form.volume} onChange={set("volume")} sx={fieldSx}>
            {CALL_VOLUMES.map((v) => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="What would you automate first?"
            required
            fullWidth
            multiline
            rows={4}
            value={form.message}
            onChange={set("message")}
            placeholder="Tell us how we can help you…"
            sx={fieldSx}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={status === "sent"}
            sx={{
              mt: 0.75,
              minHeight: {xs: 52, md: 56},
              bgcolor: c.ink,
              color: c.white,
              borderRadius: "980px",
              textTransform: "none",
              fontSize: 15,
              fontWeight: 500,
              py: 1.75,
              boxShadow: "none",
              "&:hover": {bgcolor: c.purpleDeep, boxShadow: "none"},
            }}
          >
            {label}
          </Button>

          <Typography sx={{fontSize: "11.5px", color: c.inkMuted, lineHeight: 1.5}}>
            We reply within one business day. No newsletter, no reselling your data.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
